import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { brl, fmtDate } from "@/lib/format";
import { STATUS } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Plus, ChevronRight, Loader2, Phone, MessageCircle, ShoppingBag } from "lucide-react";

const digits = (s) => (s || "").replace(/\D/g, "");

export default function ClienteHistorico() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/cliente`, { params: { key: decodeURIComponent(key) } });
      setData(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      navigate("/painel");
    }
  };
  useEffect(() => { load(); }, [key]);

  if (!data) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div></AdminLayout>;

  const c = data.cliente || {};
  const tel = digits(c.telefone || c.whatsapp);
  const wpp = digits(c.whatsapp || c.telefone);

  return (
    <AdminLayout>
      <button onClick={() => navigate("/painel")} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"><ArrowLeft className="w-4 h-4" /> Voltar</button>

      <div className="rounded-2xl border border-border bg-card p-6 mb-5">
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${(STATUS[data.status] || STATUS.pendente).dot}`} />
          <h1 className="font-display text-3xl font-black text-foreground" data-testid="cliente-nome">{data.nome}</h1>
        </div>
        <div className="mt-1 text-sm text-muted-foreground space-y-0.5">
          {c.telefone && <p>Telefone: {c.telefone}</p>}
          {(c.endereco || c.cidade) && <p>{c.endereco} {c.numero} · {c.bairro} {c.cidade}/{c.estado}</p>}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          <Info label="Compras" value={data.num_fichas} />
          <Info label="Total comprado" value={brl(data.total_compras)} />
          <Info label="Recebido" value={brl(data.recebido_total)} cls="text-green-400" />
          <Info label="Saldo devedor" value={brl(data.saldo_total)} cls={data.saldo_total > 0 ? "text-red-400" : "text-green-400"} />
        </div>

        <div className="flex flex-wrap gap-2 mt-5">
          <a href={tel ? `tel:${tel}` : "#"} data-testid="cliente-ligar"><Button variant="outline" className="rounded-full" style={{ height: 44 }}><Phone className="w-4 h-4" /> Ligar</Button></a>
          <a href={`https://wa.me/${wpp}`} target="_blank" rel="noreferrer" data-testid="cliente-whatsapp"><Button variant="outline" className="rounded-full text-green-400 border-green-500/30" style={{ height: 44 }}><MessageCircle className="w-4 h-4" /> WhatsApp</Button></a>
          <Button onClick={() => navigate(`/painel/nova-ficha?cliente=${encodeURIComponent(data.nome)}&key=${encodeURIComponent(data.key)}`)} className="rounded-full font-semibold" style={{ height: 44 }} data-testid="nova-compra-button"><Plus className="w-4 h-4" /> Nova compra</Button>
        </div>
      </div>

      <h2 className="font-display text-xl font-bold text-foreground mb-3">Relatório de compras</h2>
      <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="compras-list">
        {data.fichas.map((f, i) => {
          const st = STATUS[f.status] || STATUS.pendente;
          return (
            <button key={f.id} data-testid={`compra-row-${f.numero}`} onClick={() => navigate(`/painel/ficha/${f.id}`)}
              className={`w-full flex items-center justify-between px-5 py-4 text-left hover:bg-secondary transition-colors ${i !== data.fichas.length - 1 ? "border-b border-border" : ""}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${st.dot}`} />
                <div className="min-w-0">
                  <p className="font-semibold text-foreground truncate flex items-center gap-1.5"><ShoppingBag className="w-3.5 h-3.5" /> Ficha #{f.numero}</p>
                  <p className="text-sm text-muted-foreground truncate">{fmtDate(f.created_at).slice(0, 10)} · Total {brl(f.total_compra)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <p className={`font-display font-bold ${f.saldo > 0 ? "text-red-400" : "text-green-400"}`}>{brl(f.saldo)}</p>
                  <p className={`text-xs font-medium ${st.text}`}>{st.label}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </button>
          );
        })}
      </div>
    </AdminLayout>
  );
}

const Info = ({ label, value, cls = "text-foreground" }) => (
  <div className="rounded-xl bg-white/5 p-3">
    <p className="text-muted-foreground text-xs">{label}</p>
    <p className={`font-display font-bold text-lg ${cls}`}>{value}</p>
  </div>
);
